import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, Phone, Mail, Share2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { useLanguage } from "../contexts/LanguageContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Telegram Icon Component
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 0 0-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
  </svg>
);

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSocialDropdownOpen, setIsSocialDropdownOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const { toast } = useToast();
  const socialDropdownRef = useRef(null);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      setSettings({
        phone: '+7 (495) 123-45-67',
        email: 'mail@velox-hint.com',
        whatsapp: '+79001234567',
        telegram: 'https://t.me/veloxhint'
      });
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (socialDropdownRef.current && !socialDropdownRef.current.contains(event.target)) {
        setIsSocialDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigationItems = [
    { name: currentLanguage === 'RU' ? 'Услуги' : 'Services', href: "/services" },
    { name: currentLanguage === 'RU' ? 'Документы' : 'Documents', href: "/documents" },
    { name: currentLanguage === 'RU' ? 'Новости' : 'News', href: "/articles" },
    { name: currentLanguage === 'RU' ? 'FAQ' : 'FAQ', href: "/faq" },
    { name: currentLanguage === 'RU' ? 'О компании' : 'About', href: "/about" },
    { name: t('contacts'), href: "/#contact", internal: true }
  ];

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    toast({
      title: t('languageChanged'),
      description: t('reloadForChanges'),
    });
  };

      const handleNavClick = (href, e) => {
    const currentPath = window.location.pathname;
    if (href === currentPath || (href === '/' && currentPath === '/')) {
      if (e && e.preventDefault) e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToContact = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#contact';
    }
  };

  // Автоматический плавный скролл к контактам при переходе с других страниц
  React.useEffect(() => {
    if (window.location.hash === '#contact') {
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300); // Небольшая задержка, чтобы страница успела полностью отрисоваться
    }
  }, [window.location.hash]);

  const handleCopyEmail = async () => {
    const email = settings.email || "mail@velox-hint.com";
    try {
      await navigator.clipboard.writeText(email);
      toast({
        title: t('emailCopied'),
        description: email,
      });
    } catch (err) {
      console.error("Failed to copy email: ", err);
    }
  };

  const handleCopyPhone = async () => {
    const phone = settings.phone || "+7 (495) 123-45-67";
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.location.href = `tel:${phone.replace(/[^\d+]/g, '')}`;
    } else {
      try {
        await navigator.clipboard.writeText(phone);
        toast({
          title: t('phoneCopied'),
          description: phone,
        });
      } catch (err) {
        console.error("Failed to copy phone: ", err);
      }
    }
  };

  const handleTelegramClick = () => {
    const telegramUrl = settings.telegram || "https://t.me/veloxhint";
    window.open(telegramUrl, '_blank');
    setIsSocialDropdownOpen(false);
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = (settings.whatsapp || "79001234567").replace(/[^\d]/g, '');
    window.open(`https://wa.me/${phoneNumber}`, '_blank');
    setIsSocialDropdownOpen(false);
  };

  const whatsappPhone = settings.whatsapp || "+7 900 123-45-67";
  const telegramUsername = settings.telegram ? settings.telegram.replace('https://t.me/', '@') : '@veloxhint';

  return (
    <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top bar */}
        <div className="hidden lg:flex items-center justify-between py-2 text-sm text-gray-600 border-b border-gray-50">
          <div className="flex items-center space-x-6">
            <button onClick={handleCopyPhone} className="flex items-center space-x-2 hover:text-orange-500 transition">
              <Phone className="h-4 w-4" />
              <span>{settings.phone || '+7 (495) 123-45-67'}</span>
            </button>
            <button onClick={handleCopyEmail} className="flex items-center space-x-2 hover:text-orange-500 transition">
              <Mail className="h-4 w-4" />
              <span>{settings.email || 'mail@velox-hint.com'}</span>
            </button>
          </div>
          <div className="text-sm">
            {t('freeConsultation')}
          </div>
        </div>

        {/* Main navigation */}
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" onClick={(e) => handleNavClick("/", e)} className="flex-shrink-0">
              <h1 className="text-2xl font-bold text-gray-900">
                ВЕЛОКС<span className="text-orange-500">ХИНТ</span>
              </h1>
            </Link>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigationItems.map((item) => (
              item.internal ? (
                <button
                  key={item.name}
                  onClick={() => {
                    const section = document.getElementById('contact');
                    if (section) { section.scrollIntoView({ behavior: 'smooth' }); } else { window.location.href = '/#contact'; }
                  }}
                  className="text-gray-700 hover:text-orange-500 px-3 py-2 text-sm font-medium transition-colors duration-200"
                >
                  {item.name}
                </button>
              ) : (
                <Link
                  key={item.name}
                  to={item.href} onClick={(e) => handleNavClick(item.href, e)}
                  className="text-gray-700 hover:text-orange-500 px-3 py-2 text-sm font-medium transition-colors duration-200"
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            {/* Language selector */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handleLanguageChange("RU")}
                className={`px-2 py-1 text-sm font-medium rounded transition-colors duration-200 ${
                  currentLanguage === "RU" 
                    ? "bg-orange-100 text-orange-600" 
                    : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                }`}
              >
                RU
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => handleLanguageChange("EN")}
                className={`px-2 py-1 text-sm font-medium rounded transition-colors duration-200 ${
                  currentLanguage === "EN" 
                    ? "bg-orange-100 text-orange-600" 
                    : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                }`}
              >
                EN
              </button>
            </div>

            <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={scrollToContact}>
              {t('consultation')}
            </Button>
            
            {/* Contact icons */}
            <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-200">
              <button
                onClick={handleCopyPhone}
                className="p-2 text-gray-600 hover:text-orange-500 hover:bg-orange-50 rounded-full transition-all duration-200"
                title={settings.phone}
              >
                <Phone className="h-5 w-5" />
              </button>
              <button
                onClick={handleCopyEmail}
                className="p-2 text-gray-600 hover:text-orange-500 hover:bg-orange-50 rounded-full transition-all duration-200"
                title={settings.email}
              >
                <Mail className="h-5 w-5" />
              </button>
              
              {/* Social Networks Dropdown */}
              <div className="relative" ref={socialDropdownRef}>
                <button
                  onClick={() => setIsSocialDropdownOpen(!isSocialDropdownOpen)}
                  className={`p-2 rounded-full transition-all duration-200 ${
                    isSocialDropdownOpen 
                      ? 'text-orange-500 bg-orange-50' 
                      : 'text-gray-600 hover:text-orange-500 hover:bg-orange-50'
                  }`}
                  title={currentLanguage === 'RU' ? 'Социальные сети' : 'Social Networks'}
                >
                  <Share2 className="h-5 w-5" />
                </button>
                
                {/* Dropdown Menu */}
                {isSocialDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <span className="text-xs font-semibold text-gray-500 uppercase">
                        {currentLanguage === 'RU' ? 'Мессенджеры' : 'Messengers'}
                      </span>
                    </div>
                    
                    <button
                      onClick={handleTelegramClick}
                      className="w-full flex items-center px-4 py-3 hover:bg-blue-50 transition-colors"
                    >
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <TelegramIcon className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">Telegram</div>
                        <div className="text-xs text-gray-500">{telegramUsername}</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleWhatsAppClick}
                      className="w-full flex items-center px-4 py-3 hover:bg-green-50 transition-colors"
                    >
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <WhatsAppIcon className="h-5 w-5 text-green-500" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">WhatsApp</div>
                        <div className="text-xs text-gray-500">{whatsappPhone}</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-gray-100">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigationItems.map((item) => (
              item.internal ? (
                <button
                  key={item.name}
                  onClick={() => {
                    setIsMenuOpen(false);
                    const section = document.getElementById('contact');
                    if (section) { section.scrollIntoView({ behavior: 'smooth' }); } else { window.location.href = '/#contact'; }
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-orange-500 hover:bg-gray-50"
                >
                  {item.name}
                </button>
              ) : (
                <Link
                  key={item.name}
                  to={item.href} onClick={(e) => handleNavClick(item.href, e)}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-orange-500 hover:bg-gray-50"
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>
          
          <div className="px-4 py-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">{t('language')}</span>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    handleLanguageChange("RU");
                    setIsMenuOpen(false);
                  }}
                  className={`px-3 py-1 text-sm font-medium rounded ${
                    currentLanguage === "RU" ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-700"
                  }`}
                >
                  RU
                </button>
                <button
                  onClick={() => {
                    handleLanguageChange("EN");
                    setIsMenuOpen(false);
                  }}
                  className={`px-3 py-1 text-sm font-medium rounded ${
                    currentLanguage === "EN" ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-700"
                  }`}
                >
                  EN
                </button>
              </div>
            </div>
            
            <Button 
              size="sm" 
              className="w-full bg-orange-500 hover:bg-orange-600"
              onClick={(e) => {
                setIsMenuOpen(false);
                scrollToContact(e);
              }}
            >
              {t('consultation')}
            </Button>

            {/* Mobile Contact Icons */}
            <div className="flex items-center justify-center space-x-4 mt-4">
              <button onClick={handleCopyPhone} className="p-2 text-gray-600 hover:text-orange-500 rounded-full">
                <Phone className="h-6 w-6" />
              </button>
              <button onClick={handleCopyEmail} className="p-2 text-gray-600 hover:text-orange-500 rounded-full">
                <Mail className="h-6 w-6" />
              </button>
            </div>
            
            {/* Mobile Social Links */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3 text-center">
                {currentLanguage === 'RU' ? 'Мессенджеры' : 'Messengers'}
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    handleTelegramClick();
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center px-3 py-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <TelegramIcon className="h-5 w-5 text-blue-500 mr-3" />
                  <div className="text-left">
                    <div className="text-sm font-medium text-gray-900">Telegram</div>
                    <div className="text-xs text-gray-500">{telegramUsername}</div>
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    handleWhatsAppClick();
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center px-3 py-2 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <WhatsAppIcon className="h-5 w-5 text-green-500 mr-3" />
                  <div className="text-left">
                    <div className="text-sm font-medium text-gray-900">WhatsApp</div>
                    <div className="text-xs text-gray-500">{whatsappPhone}</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
